import { Map } from "mapbox-gl";
import { BaseControl } from "react-map-gl";
import type { MapControlProps } from "react-map-gl/src/components/use-map-control";

interface Props extends MapControlProps {
  onDrop: (
    e: DragEvent,
    location: { x: number; y: number; coordinates: [number, number] },
    map: Map
  ) => void;
}

export class DragOverlay extends BaseControl<Props, HTMLDivElement> {
  private _dragActive: boolean = false;

  _onGlobalDragOver = (e: DragEvent) => {
    // Activate overlay when dragging our icons, even if pointerEvents are none
    const types = (e.dataTransfer && Array.from(e.dataTransfer.types)) || [];
    if (types.includes("map/icon-id")) {
      if (!this._dragActive) {
        this._dragActive = true;
        this.forceUpdate();
      }
    }
  };

  _onGlobalDragEnd = () => {
    if (this._dragActive) {
      this._dragActive = false;
      this.forceUpdate();
    }
  };

  _onDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
    if (!this._dragActive) {
      this._dragActive = true;
      this.forceUpdate();
    }
  };

  _onDragEnter = (e) => {
    e.preventDefault();
    if (!this._dragActive) {
      this._dragActive = true;
      this.forceUpdate();
    }
  };

  _onDragLeave = (e) => {
    e.preventDefault();
    if (this._dragActive) {
      this._dragActive = false;
      this.forceUpdate();
    }
  };

  _onDrop = (e) => {
    const map = this._context.map;
    const container = this._containerRef.current as HTMLDivElement | null;
    if (!container || !map) return;

    e.preventDefault();
    const rect = container.getBoundingClientRect();
    const scaleX = rect.width / container.offsetWidth;
    const scaleY = rect.height / container.offsetHeight;
    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;

    // convert screen coords to map coords
    const coordinates = map.unproject([x, y]).toArray();

    this.props.onDrop(e, { x, y, coordinates }, map);
    if (this._dragActive) {
      this._dragActive = false;
      this.forceUpdate();
    }
  };

  componentDidMount() {
    const container = this._containerRef.current as HTMLDivElement | null;
    if (!container) return;

    window.addEventListener("dragover", this._onGlobalDragOver);
    window.addEventListener("drop", this._onGlobalDragEnd);
    window.addEventListener("dragend", this._onGlobalDragEnd);

    container.addEventListener("dragenter", this._onDragEnter);
    container.addEventListener("dragover", this._onDragOver);
    container.addEventListener("dragleave", this._onDragLeave);
    container.addEventListener("drop", this._onDrop);
  }

  componentWillUnmount() {
    const container = this._containerRef.current as HTMLDivElement | null;
    if (!container) return;

    window.removeEventListener("dragover", this._onGlobalDragOver);
    window.removeEventListener("drop", this._onGlobalDragEnd);
    window.removeEventListener("dragend", this._onGlobalDragEnd);

    container.removeEventListener("dragenter", this._onDragEnter);
    container.removeEventListener("dragover", this._onDragOver);
    container.removeEventListener("dragleave", this._onDragLeave);
    container.removeEventListener("drop", this._onDrop);
  }

  _render() {
    return (
      <div
        ref={this._containerRef as unknown as React.LegacyRef<HTMLDivElement>}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          zIndex: 10,
          pointerEvents: this._dragActive ? "auto" : "none"
        }}
      />
    );
  }
}
